import { useEffect, useState, useCallback } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Inbox } from "lucide-react";
import { api } from "../lib/api";

const TOKEN_KEY = "finka_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchBookings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/bookings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load bookings");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBookings([]);
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}?status=${status}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status } : b))
      );
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  if (!token) {
    return (
      <div
        data-testid="admin-login-page"
        className="pt-32 md:pt-36 px-6 md:px-12 lg:px-20 pb-24 min-h-screen flex items-start justify-center"
      >
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md border border-[#D8D4CC] bg-[#FFFFFF] p-10"
        >
          <div className="w-10 h-10 border border-[#7B8871] flex items-center justify-center mb-6">
            <Lock strokeWidth={1.25} className="w-5 h-5 text-[#7B8871]" />
          </div>
          <p className="font-eyebrow text-[10px] text-[#5C5C59]">
            Finka · Private
          </p>
          <h1 className="font-serif-display text-4xl text-[#1A1A1A] mt-3">
            Admin <em className="editorial-italic text-[#7B8871]">access.</em>
          </h1>
          <div className="mt-8">
            <label className="font-eyebrow text-[10px] text-[#5C5C59]">
              Password
            </label>
            <input
              data-testid="admin-password-input"
              type="password"
              className="input-underline"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
          </div>
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loggingIn || !password}
            className="mt-8 w-full bg-[#1A1A1A] text-[#F7F5F2] py-4 font-eyebrow text-[11px] hover:bg-[#7B8871] transition-colors duration-500 disabled:opacity-50"
          >
            {loggingIn ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  const filtered =
    statusFilter === "all"
      ? bookings
      : bookings.filter((b) => b.status === statusFilter);

  const stats = {
    total: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    completed: bookings.filter((b) => b.status === "completed").length,
  };

  return (
    <div
      data-testid="admin-dashboard"
      className="pt-32 md:pt-36 px-6 md:px-12 lg:px-20 pb-24 min-h-screen"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-6">
        <div>
          <p className="font-eyebrow text-[11px] text-[#5C5C59]">
            Admin · Bookings
          </p>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[#1A1A1A] mt-3">
            Your <em className="editorial-italic text-[#7B8871]">calendar.</em>
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="admin-refresh-btn"
            onClick={fetchBookings}
            className="font-eyebrow text-[11px] inline-flex items-center gap-2 border border-[#1A1A1A] text-[#1A1A1A] px-5 py-3 hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors"
          >
            <RefreshCw
              strokeWidth={1.5}
              className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="font-eyebrow text-[11px] inline-flex items-center gap-2 text-[#5C5C59] hover:text-[#1A1A1A]"
          >
            <LogOut strokeWidth={1.5} className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
        {[
          { label: "Total", value: stats.total },
          { label: "Pending", value: stats.pending },
          { label: "Confirmed", value: stats.confirmed },
          { label: "Completed", value: stats.completed },
        ].map((s) => (
          <div
            key={s.label}
            className="border-t border-[#D8D4CC] pt-5"
            data-testid={`admin-stat-${s.label.toLowerCase()}`}
          >
            <p className="font-eyebrow text-[10px] text-[#5C5C59]">{s.label}</p>
            <p className="font-serif-display text-5xl text-[#1A1A1A] mt-2">
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {/* Filter pills */}
      <div className="mt-12 flex items-center gap-3 flex-wrap">
        {["all", "pending", "confirmed", "completed", "cancelled"].map((s) => (
          <button
            key={s}
            data-testid={`admin-filter-${s}`}
            onClick={() => setStatusFilter(s)}
            className={`px-4 py-2 font-eyebrow text-[10px] border transition-colors capitalize ${
              statusFilter === s
                ? "bg-[#1A1A1A] text-[#F7F5F2] border-[#1A1A1A]"
                : "border-[#D8D4CC] text-[#5C5C59] hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Bookings table */}
      <div className="mt-10 border border-[#D8D4CC] bg-[#FFFFFF] overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="py-20 text-center">
            <Inbox
              strokeWidth={1.25}
              className="w-8 h-8 text-[#B5BCAD] mx-auto"
            />
            <p className="font-eyebrow text-[10px] text-[#5C5C59] mt-4">
              No bookings yet
            </p>
          </div>
        ) : (
          <table className="w-full" data-testid="admin-bookings-table">
            <thead>
              <tr className="border-b border-[#D8D4CC]">
                {["When", "Service", "Guest", "Status", "Update"].map((h) => (
                  <th
                    key={h}
                    className="text-left font-eyebrow text-[10px] text-[#5C5C59] px-5 py-4"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr
                  key={b.id}
                  data-testid={`admin-row-${b.id}`}
                  className="border-b border-[#EFECE5] hover:bg-[#F7F5F2]/60"
                >
                  <td className="px-5 py-5 align-top">
                    <p className="font-serif-display text-xl text-[#1A1A1A] leading-tight">
                      {format(parseISO(b.date), "EEE, MMM d")}
                    </p>
                    <p className="text-[#5C5C59] text-sm mt-1">{b.time}</p>
                  </td>
                  <td className="px-5 py-5 align-top">
                    <p className="text-[#1A1A1A] text-sm">{b.service_name}</p>
                  </td>
                  <td className="px-5 py-5 align-top">
                    <p className="text-[#1A1A1A] text-sm">{b.name}</p>
                    <p className="text-[#5C5C59] text-xs mt-1">{b.email}</p>
                    {b.phone && (
                      <p className="text-[#5C5C59] text-xs">{b.phone}</p>
                    )}
                    {b.notes && (
                      <p className="text-[#5C5C59] text-xs italic mt-2 max-w-xs">
                        "{b.notes}"
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-5 align-top">
                    <span
                      className={`inline-block px-3 py-1 font-eyebrow text-[9px] border capitalize ${
                        b.status === "confirmed"
                          ? "border-[#7B8871] text-[#42493D] bg-[#EFECE5]"
                          : b.status === "completed"
                          ? "border-[#1A1A1A] text-[#1A1A1A]"
                          : b.status === "cancelled"
                          ? "border-[#D8D4CC] text-[#5C5C59]"
                          : "border-[#D8D4CC] text-[#5C5C59]"
                      }`}
                    >
                      {b.status}
                    </span>
                  </td>
                  <td className="px-5 py-5 align-top">
                    <select
                      data-testid={`admin-status-select-${b.id}`}
                      value={b.status}
                      onChange={(e) => updateStatus(b.id, e.target.value)}
                      className="font-eyebrow text-[10px] border border-[#D8D4CC] bg-transparent px-3 py-2"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
